import { useEffect, useState, useRef } from 'react'
import { SettingsLayout } from '~/Layouts';
import {
    Button,
    SaveButtons,
    Tooltip
} from '~/Common';
import {
    Alert,
    Box,
    Close,
    Grid,
    Heading,
    Spinner,
    Text,
    Flex,
    Link,
    Divider,
} from '@theme-ui/components';
import { Field } from '~/Forms/Field';
import { Form, Formik } from 'formik';
import { toast } from 'utils/toast'
import { Yup } from 'utils/validationRules';
import moment from 'moment-timezone'
import { useDispatch, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group'
import { utilStateByCountry } from 'features/theme/themeSlice'
import {
    storeInstitutionDetails,
    setUser,
    storeInstitutionSSI,
} from 'features/auth/authSlice';
import { Icon } from 'assets/Icon';
import { Submit } from '~/Forms';
import { NavLink } from 'react-router-dom/cjs/react-router-dom';

const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    designation: Yup.string().nullable(),
    admin_email: Yup.string().email().required('required field'),
    admin_phone: Yup.string().required('required field'),
    afsl: Yup.string().required('required field'),
    car: Yup.string().nullable(),
    pid: Yup.string().required('required field'),
    unit_number: Yup.string().nullable(),
    street_number: Yup.string().nullable(),
    street_name: Yup.string().nullable(),
    street_type: Yup.string().nullable(),
    address2: Yup.string().nullable(),
    state: Yup.string().nullable(),
    suburb: Yup.string().nullable(),
    postcode: Yup.string().nullable(),
    country: Yup.string().nullable(),
});

const mapStateToProps = (state) => {
    return { user: state.user }
}

const SettingsInstitution = ({ user }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const nodeRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [stateOptions, setStateOptions] = useState([]);
    const [showUploadStandardSettlementInstructions, setShowUploadStandardSettlementInstructions] = useState(false);
    const [ssiUploadProgress, setSsiUploadProgress] = useState(0);

    moment.tz.setDefault(user.user?.timezone);

    useEffect(() => {
        let isMounted = true;
    
        setLoading(true);
    
        utilStateByCountry('AU')
            .then((response) => {
                if (isMounted) {
                    setStateOptions(response?.data);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setStateOptions(null);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setLoading(false);
                }
            });
    
        return () => {
            isMounted = false;
        };
    }, []); 


    const handleSubmit = (values, actions) => {
        storeInstitutionDetails({
            device_name: 'App',
            name: values.name,
            designation: values.designation,
            admin_email: values.admin_email,
            admin_phone: values.admin_phone,
            afsl: values.afsl,
            car: values.car,
            pid: values.pid,
            address: values.address,
            unit_number: values.unit_number,
            street_number: values.street_number,
            street_name: values.street_name,
            street_type: values.street_type,
            address2: values.address2,
            state: values.state,
            suburb: values.suburb,
            postcode: values.postcode,
            country: values.country ?? 'AU',
        }).then((response) => {
            toast.success(response?.message || t('settings.account.success'), {
                toastId: 'account-success',
            });
            dispatch(setUser(response));
            actions.setSubmitting(false);
        }).catch(({ response }) => {
            response?.data?.errors && actions.setErrors(response.data.errors);
            toast.error(response?.data?.message || t('settings.account.error'), {
                toastId: 'account-error',
            });
            actions.setStatus('api_error');
            actions.setSubmitting(false);
        });
    };

    const onUploadProgress = (p) => {
        setSsiUploadProgress((p.loaded / p.total) * 100);
    };

    const handleUploadStandardSettlementInstructions = ({ name, value, setSubmitting, setStatus }) => {
        let formdata = new FormData();
        formdata.append(name, value);

        setSsiUploadProgress(0);

        storeInstitutionSSI(formdata, onUploadProgress).then((response) => {
            toast.success(t('settings.institution.ssi.success'), {
                toastId: 'ssi-success',
            });
            dispatch(setUser(response));

            setTimeout(() => setShowUploadStandardSettlementInstructions(false), 750);
        }).catch(({ response }) => {
            toast.error(response?.data?.message || t('settings.institution.ssi.error'), {
                toastId: 'ssi-error',
            });
            setStatus('api_error');

            setTimeout(() => setShowUploadStandardSettlementInstructions(false), 750);
        });
    };


    return (
        <SettingsLayout>
            {loading ? <Box id="settingsLoading"><Spinner variant="styles.spinner" m={'auto'} /></Box> : (
                <>
                    <CSSTransition
                        nodeRef={nodeRef}
                        in={true}
                        timeout={500}
                        classNames="fade"
                        unmountOnExit
                        appear
                    >
                        <Box ref={nodeRef}>
                            <Heading as="h1" variant="h2">{t('Institution Details')}</Heading>

                            <Formik
                                enableReinitialize
                                validateOnMount={true}
                                initialValues={{
                                    name: user?.user?.institution?.name ?? '',
                                    designation: user?.user?.institution?.designation ?? '',
                                    admin_email: user?.user?.institution?.admin_email ?? '',
                                    admin_phone: user?.user?.institution?.admin_phone ?? '',
                                    afsl: user?.user?.institution?.afsl ?? '',
                                    car: user?.user?.institution?.car ?? '',
                                    pid: user?.user?.institution?.pid ?? '',
                                    unit_number: user?.user?.institution?.address?.unit_number ?? '',
                                    street_number: user?.user?.institution?.address?.street_number ?? '',
                                    street_name: user?.user?.institution?.address?.street_name ?? '',
                                    street_type: user?.user?.institution?.address?.street_type ?? '',
                                    address2: user?.user?.institution?.address?.address2 ?? '',
                                    state: user?.user?.institution?.address?.state ?? '',
                                    suburb: user?.user?.institution?.address?.suburb ?? '',
                                    postcode: user?.user?.institution?.address?.postcode ?? '',
                                    country: user?.user?.institution?.address?.country ?? 'AU',
                                }}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ status, setStatus, setFieldValue }) => (
                                    <Box>
                                        {status === 'api_error' ?
                                            <Alert role="alert" variant='danger' mb={2}>
                                                {t('settings.account.error')}
                                                <Close ml="auto" mr={-2} onClick={() => setStatus(null)} />
                                            </Alert> : ''}
                                        <Flex sx={{ mb: '33px', alignItems: 'center' }}>
                                            <Box>
                                                <Text as="div" sx={{ fontSize: '20px', lineHeight: '24px' }}>
                                                    {user?.user?.institution?.name}
                                                </Text>
                                                <Text as="div" sx={{ fontSize: '16px', lineHeight: '18.2px' }}>
                                                    {user?.user?.institution?.admin_email}
                                                </Text>
                                                <Box sx={{ lineHeight: 1 }}>
                                                    <Tooltip text={t('Liquidity Account Number')} sx={{ cursor: 'default' }}>
                                                        <Text
                                                            as="div"
                                                            sx={{
                                                                fontSize: 11,
                                                                color: 'dark',
                                                            }}
                                                        >
                                                            {user.user?.account_number}
                                                        </Text>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </Flex>
                                        <Form>
                                            <Heading as="h3" variant="settings_heading" mb={3}>{t('Institution Details')}</Heading>
                                            <Grid mb={1} sx={{
                                                gridGap: [2, 3],
                                                gridTemplateColumns: ['1fr', 'repeat(2, 1fr)'],
                                                width: '100%'
                                            }}>
                                                <Field
                                                    label="Name"
                                                    name="name"
                                                    required="true"
                                                />
                                                <Box sx={{ position: 'relative' }}>
                                                    <Field
                                                        label="Designation"
                                                        name="designation"
                                                    />
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        height: '70px',
                                                        top: 0,
                                                        right: 3,
                                                        pointerEvents: 'none',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center'
                                                    }}>
                                                        <Text>A/C</Text>
                                                    </Box>
                                                </Box>
                                            </Grid>

                                            <Grid mb={1} sx={{
                                                gridGap: [2, 3],
                                                gridTemplateColumns: ['1fr', 'repeat(2, 1fr)'],
                                                width: '100%'
                                            }}>
                                                <Field
                                                    label="Admin Email"
                                                    name="admin_email"
                                                    required="true"
                                                />
                                                <Field
                                                    label="Admin Phone Number"
                                                    name="admin_phone"
                                                    required="true"
                                                />
                                            </Grid>

                                            <Grid mb={1} sx={{
                                                gridGap: [2, 3],
                                                gridTemplateColumns: ['1fr', 'repeat(2, 1fr)'],
                                                width: '100%'
                                            }}>
                                                <Field
                                                    label="AFSL"
                                                    name="afsl"
                                                    required="true"
                                                />
                                                <Field
                                                    label="CAR"
                                                    name="car"
                                                />
                                            </Grid>

                                            <Grid mb={1} sx={{
                                                gridGap: [2, 3],
                                                gridTemplateColumns: ['1fr', 'repeat(2, 1fr)'],
                                                width: '100%'
                                            }}>
                                                <Field
                                                    label="PID"
                                                    name="pid"
                                                    required="true"
                                                />
                                            </Grid>

                                            <Heading as="h3" variant="settings_heading" mb={3}>{t('Institution Address')}</Heading>

                                            <Grid mb={1} sx={{
                                                gridGap: [2, 3],
                                                gridTemplateColumns: ['1fr'],
                                                width: '100%'
                                            }}>
                                                <Field
                                                    field="google_places"
                                                    label="Search for address..."
                                                    name="address"
                                                    setFieldValue={setFieldValue}
                                                />
                                            </Grid>

                                            <Grid mb={1} sx={{
                                                gridGap: [2],
                                                gridTemplateColumns: ['1fr', '1fr 1fr 2fr 1fr'],
                                                width: '100%'
                                            }}>
                                                <Field
                                                    label="Unit Number"
                                                    name="unit_number"
                                                />
                                                <Field
                                                    label="Street Number"
                                                    name="street_number"
                                                />

                                                <Field
                                                    label="Street Name"
                                                    name="street_name"
                                                />
                                                <Field
                                                    label="Street Type"
                                                    name="street_type"
                                                />
                                            </Grid>

                                            <Grid mb={1} sx={{
                                                gridGap: [2, 3],
                                                gridTemplateColumns: ['1fr'],
                                                width: '100%'
                                            }}>
                                                <Field
                                                    label="Address second line"
                                                    name="address2"
                                                />
                                            </Grid>

                                            <Grid sx={{
                                                gridGap: [2],
                                                gridTemplateColumns: ['1fr', 'repeat(3, 1fr)'],
                                                width: '100%'
                                            }}>
                                                <Field
                                                    label="Suburb"
                                                    name="suburb"
                                                />
                                                <Field
                                                    field={Boolean(Array.isArray(stateOptions) && stateOptions.length > 0) ? 'select' : 'text'}
                                                    label="State"
                                                    name="state"
                                                    options={stateOptions}
                                                />

                                                <Field
                                                    label="Postcode"
                                                    name="postcode"
                                                />
                                            </Grid>

                                            <Divider color="#D5D9DA" mt="20px" mb="36px" />

                                            <Box>
                                                <Heading as="h3" variant="settings_heading" mb={3}>{t('Standard Settlement Instructions (SSI)')}</Heading>

                                                <Box mb={4}>
                                                    {showUploadStandardSettlementInstructions ? (
                                                        <>
                                                            <Field
                                                                field="upload"
                                                                name="standard_settlement_instructions"
                                                                label="Upload Standard Settlement Instructions (SSI)"
                                                                showLabel={false}
                                                                uploadProgress={ssiUploadProgress}
                                                                onChange={(props) => handleUploadStandardSettlementInstructions({ setStatus, ...props })}
                                                            />
                                                            <Box mb={4}>
                                                                <Link to="#cancel" onClick={(e) => {
                                                                    e.preventDefault();
                                                                    setShowUploadStandardSettlementInstructions(false)
                                                                }}>{t('Cancel SSI upload')}</Link>
                                                            </Box>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Flex sx={{ alignItems: 'center', mb: 2, gridGap: 3 }}>
                                                                <Button variant="light" onClick={() => setShowUploadStandardSettlementInstructions(true)}>{t(user.user?.institution?.standard_settlement_instructions ? 'Update SSI' : 'Supply SSI')}</Button>
                                                                {user.user?.institution?.standard_settlement_instructions &&
                                                                    <Box variant="styles.captionWrapper" sx={{ py: '13px' }}>
                                                                        <Flex sx={{ alignItems: 'center' }}>
                                                                            <Icon icon="tick" color="tealDark" width="auto" height="9px" sx={{ mr: 2 }} />
                                                                            <Text variant="caption">{t(user.user?.institution?.ssi_status === 'Required' ? 'Requires Upload' : 'SSI Document Provided')}</Text>
                                                                        </Flex>
                                                                    </Box>
                                                                }
                                                            </Flex>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>

                                            <SaveButtons>
                                                <Submit
                                                    variant="primary"
                                                    text={t('buttons.save')}
                                                    sx={{
                                                        width: '200px'
                                                    }}
                                                    showErrorTooltip={true}
                                                />
                                                <Button
                                                    as={NavLink}
                                                    to="/institution/dashboard"
                                                    variant="light"
                                                    sx={{
                                                        width: '200px'
                                                    }}
                                                >
                                                    {t('buttons.discard')}
                                                </Button>
                                            </SaveButtons>
                                        </Form>
                                    </Box>
                                )}
                            </Formik>
                        </Box>
                    </CSSTransition>
                </>
            )}
        </SettingsLayout>
    );
};

export default connect(mapStateToProps, null)(SettingsInstitution);
